import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Statut, Segmente, Icone, quandRelance, libelleType, libelleEchange, dateCourte, dateLongue,
  aujourdhui, aller, prenom, messageErreur,
} from './commun';

// Page d'accueil du commercial : ce qu'il doit faire aujourd'hui, et rien d'autre.
function LigneRelance({ c, equipe }) {
  const q = quandRelance(c.prochaine_action_le, c.dans_jours);
  return (
    <div className={`relance ${q.retard ? 'en-retard' : ''}`}>
      <a className="relance-qui" href={`#/compte/${c.id}`}>
        <span className="relance-nom">{c.nom}</span>
        <span className="relance-meta">
          <Statut statut={c.statut} />
          <span>{libelleType(c.type)}{c.ville ? ` · ${c.ville}` : ''}{equipe && c.commercial_nom ? ` · ${c.commercial_nom}` : ''}</span>
        </span>
      </a>
      <a className="relance-quoi" href={`#/compte/${c.id}`}>
        <span className="relance-action">{c.prochaine_action || 'Relancer'}</span>
        <span className={`relance-quand ${q.retard ? 'rouge' : ''}`}>{q.texte}</span>
        {c.dernier_type && (
          <span className="relance-dernier">
            {libelleEchange(c.dernier_type)} le {dateCourte(c.dernier_le)}{c.derniere_note ? ` : ${c.derniere_note}` : ''}
          </span>
        )}
      </a>
      <div className="relance-boutons">
        {c.telephone && (
          <a className="bouton-tel" href={`tel:${c.telephone.replace(/[^\d+]/g, '')}`} aria-label={`Appeler ${c.nom}`}>
            <Icone.Tel taille={16} /><span className="numero">{c.telephone}</span>
          </a>
        )}
        <a className="bouton-doux" href={`#/compte/${c.id}?noter=1`}>Noter un échange</a>
      </div>
    </div>
  );
}

export default function Relances({ utilisateur }) {
  const [portee, setPortee] = useState(utilisateur.role === 'commercial' ? 'moi' : 'equipe');
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(async () => {
    try { setD((await api.get('/commercial/relances', { params: { portee } })).data); setErreur(''); }
    catch (e) { setErreur(messageErreur(e, 'Impossible de charger les relances.')); }
  }, [portee]);
  useEffect(() => { charger(); }, [charger]);

  const chercher = (e) => { e.preventDefault(); aller(`#/comptes?q=${encodeURIComponent(recherche.trim())}`); };

  const retard = d?.comptes.filter((c) => c.dans_jours < 0) || [];
  const jour = d?.comptes.filter((c) => c.dans_jours === 0) || [];
  const semaine = d?.comptes.filter((c) => c.dans_jours > 0) || [];
  const equipe = portee === 'equipe';

  return (
    <div className="page-relances">
      <div className="page-entete">
        <div>
          <h1>Bonjour {prenom(utilisateur)}</h1>
          <p className="sous-titre">
            <span className="majuscule">{dateLongue(aujourdhui())}</span>
            {d && <> · {jour.length} relance{jour.length > 1 ? 's' : ''} aujourd'hui{retard.length > 0 && <>, <span className="rouge gras">{retard.length} en retard</span></>}</>}
          </p>
        </div>
        <div className="entete-actions">
          <form className="champ-recherche" onSubmit={chercher} role="search">
            <Icone.Loupe />
            <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher un client, une ville…" aria-label="Rechercher un compte" />
          </form>
          <a className="bouton bouton-grand cache-mobile" href="#/nouveau"><Icone.Plus /> Nouveau prospect</a>
        </div>
      </div>

      <Segmente etiquette="Relances de qui" valeur={portee} onChange={setPortee}
        options={[{ cle: 'moi', libelle: 'Mes comptes' }, { cle: 'equipe', libelle: "Toute l'équipe" }]} />

      {erreur && <div className="alerte">{erreur}</div>}
      {!d && !erreur && <div className="attente">Chargement…</div>}

      {d && (
        <div className="relances-grille">
          <section className="relances-liste">
            {d.a_planifier > 0 && (
              <a className="bandeau-info" href="#/comptes?a_planifier=1">
                <strong>{d.a_planifier} prospect{d.a_planifier > 1 ? 's' : ''} sans relance prévue.</strong>{' '}
                Sans date, un prospect ne revient jamais dans cette liste. Les voir
              </a>
            )}
            {retard.length > 0 && <h2 className="section-titre rouge">En retard · {retard.length}</h2>}
            {retard.map((c) => <LigneRelance key={c.id} c={c} equipe={equipe} />)}
            <h2 className="section-titre">Aujourd'hui · {jour.length}</h2>
            {jour.length === 0 && <p className="vide-ligne">Rien de prévu aujourd'hui.</p>}
            {jour.map((c) => <LigneRelance key={c.id} c={c} equipe={equipe} />)}
            {semaine.length > 0 && <h2 className="section-titre">Les 7 prochains jours · {semaine.length}</h2>}
            {semaine.map((c) => <LigneRelance key={c.id} c={c} equipe={equipe} />)}
            {d.comptes.length === 0 && (
              <div className="vide">
                <p><strong>Aucune relance dans les 7 prochains jours.</strong></p>
                <p>Créez un prospect, ou ouvrez un compte et notez un échange : la date de relance que vous choisirez le fera apparaître ici.</p>
                <a className="bouton" href="#/nouveau">Nouveau prospect</a>
              </div>
            )}
          </section>

          <aside className="relances-cote">
            <div className="carte">
              <h2>{equipe ? "L'équipe" : 'Mes'} 30 derniers jours</h2>
              <div className="trio">
                <div><span className="chiffre">{d.stats.travailles}</span><span className="legende">prospects travaillés</span></div>
                <div><span className="chiffre vert">{d.stats.signes}</span><span className="legende">clients signés</span></div>
                <div><span className="chiffre">{d.stats.echanges}</span><span className="legende">échanges notés</span></div>
              </div>
            </div>
            {d.derniers_signes.length > 0 && (
              <div className="carte">
                <h2>Derniers clients signés</h2>
                {d.derniers_signes.map((s) => (
                  <a key={s.id} className="signe" href={`#/compte/${s.id}`}>
                    <strong>{s.nom}</strong>
                    <span>{libelleType(s.type)}{s.ville ? ` · ${s.ville}` : ''} · signé le {dateCourte(s.signe_le)}</span>
                  </a>
                ))}
              </div>
            )}
            <p className="encart">
              Chaque échange noté fixe la relance suivante. Un compte passé en « Perdu » sort de cette liste
              mais garde tout son historique.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
